import { authApi } from "./index";
import { createBooking } from "./bookings";

const LOCAL_STORAGE_KEY = "amalgamate_local_quotes_v1";

const getLocalQuotes = () => {
  const data = localStorage.getItem(LOCAL_STORAGE_KEY);
  return data ? JSON.parse(data) : [];
};

const saveLocalQuotes = (quotes) => {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(quotes));
};

export const normalizeQuoteRequest = (qr) => {
  if (!qr) return null;

  const provider = qr.provider || {
    userId: qr.providerId || "prov-1",
    name: qr.providerName || "Adventure Guide & Provider",
    avatarUrl: qr.providerAvatarUrl || "/profilePic.jpg"
  };

  const customer = qr.customer || {
    userId: qr.customerId || "cust-1",
    name: qr.customerName || "Customer User",
    avatarUrl: qr.customerAvatarUrl || "/profilePic.jpg"
  };

  return {
    ...qr,
    id: qr.id || qr.quoteRequestId,
    quoteRequestId: qr.quoteRequestId || qr.id,
    providerId: qr.providerId || provider.userId,
    customerId: qr.customerId || customer.userId,
    description: qr.description || "",
    categoryHint: qr.categoryHint || "custom",
    budgetHint: qr.budgetHint !== undefined ? qr.budgetHint : 0,
    currency: qr.currency || "USD",
    preferredStartDate: qr.preferredStartDate || "",
    preferredEndDate: qr.preferredEndDate || "",
    attachmentUrls: qr.attachmentUrls || [],
    contactPreference: qr.contactPreference || "platform",
    status: qr.status || "requested",
    createdAt: qr.createdAt || new Date().toISOString(),
    quotedPrice: qr.quotedPrice !== undefined ? qr.quotedPrice : null,
    scope: qr.scope || "",
    proposedStartDate: qr.proposedStartDate || "",
    proposedEndDate: qr.proposedEndDate || "",
    validUntil: qr.validUntil || "",
    notes: qr.notes || "",
    declineReason: qr.declineReason || "",
    declineMessage: qr.declineMessage || "",
    provider,
    customer,
    providerName: provider.name,
    providerAvatarUrl: provider.avatarUrl,
    customerName: customer.name,
    customerAvatarUrl: customer.avatarUrl
  };
};

// 1. Create a Quote Request
export const createQuoteRequest = async (payload) => {
  try {
    const response = await authApi.post("/quote-requests", payload);
    const data = response.data.data || response.data;
    return normalizeQuoteRequest(data);
  } catch (error) {
    console.warn("Backend API POST /quote-requests not reachable. Using fallback local storage.", error);

    const quotes = getLocalQuotes();
    const newId = 'QR-' + Math.floor(100000 + Math.random() * 900000);
    const currentUserId = localStorage.getItem("userId") || "cust-current-user-id";
    const currentUserName = localStorage.getItem("userName") || "Alice Customer";

    const mockQuoteRequest = {
      id: newId,
      quoteRequestId: newId,
      providerId: payload.providerId || "prov-1",
      customerId: currentUserId,
      description: payload.description || "",
      categoryHint: payload.categoryHint || "custom",
      budgetHint: payload.budgetHint || 0,
      currency: payload.currency || "USD",
      preferredStartDate: payload.preferredStartDate || "",
      preferredEndDate: payload.preferredEndDate || "",
      attachmentUrls: payload.attachmentUrls || [],
      contactPreference: payload.contactPreference || "platform",
      status: "requested",
      createdAt: new Date().toISOString(),
      provider: {
        userId: payload.providerId || "prov-1",
        name: "Adventure Club Provider",
        avatarUrl: "/profilePic.jpg"
      },
      customer: {
        userId: currentUserId,
        name: currentUserName,
        avatarUrl: "/profilePic.jpg"
      }
    };

    quotes.unshift(mockQuoteRequest);
    saveLocalQuotes(quotes);
    return normalizeQuoteRequest(mockQuoteRequest);
  }
};

// 2. Get Quote Requests List
export const getQuoteRequests = async (params = {}) => {
  try {
    const response = await authApi.get("/quote-requests", { params });
    const data = response.data.data || response.data;
    if (Array.isArray(data)) {
      return data.map(normalizeQuoteRequest);
    }
    return [];
  } catch (error) {
    console.warn("Backend API GET /quote-requests not reachable. Using fallback local storage.", error);

    let quotes = getLocalQuotes();
    const currentUserId = localStorage.getItem("userId") || "cust-current-user-id";

    if (params.role === "customer") {
      quotes = quotes.filter(q => q.customerId === currentUserId || q.customer?.userId === currentUserId);
    } else if (params.role === "provider") {
      quotes = quotes.filter(q => q.providerId === currentUserId || q.provider?.userId === currentUserId);
    }

    if (params.status) {
      if (params.status === 'pending') {
        quotes = quotes.filter(q => q.status === 'requested' || q.status === 'pending');
      } else {
        quotes = quotes.filter(q => q.status === params.status);
      }
    }

    return quotes.map(normalizeQuoteRequest);
  }
};

// 3. Get Single Quote Request Detail
export const getQuoteRequestDetails = async (id) => {
  try {
    const response = await authApi.get(`/quote-requests/${id}`);
    const data = response.data.data || response.data;
    return normalizeQuoteRequest(data);
  } catch (error) {
    console.warn(`Backend API GET /quote-requests/${id} not reachable. Using fallback local storage.`, error);

    const quotes = getLocalQuotes();
    const found = quotes.find(q => q.id === id || q.quoteRequestId === id);
    if (!found) {
      throw new Error("Quote request not found");
    }
    return normalizeQuoteRequest(found);
  }
};

// 4. Provider Sends Quote
export const sendQuote = async (id, payload) => {
  try {
    const response = await authApi.post(`/quote-requests/${id}/quote`, payload);
    const data = response.data.data || response.data;
    return normalizeQuoteRequest(data);
  } catch (error) {
    console.warn(`Backend API POST /quote-requests/${id}/quote not reachable. Updating local storage.`, error);

    const quotes = getLocalQuotes();
    const index = quotes.findIndex(q => q.id === id || q.quoteRequestId === id);
    if (index === -1) throw new Error("Quote request not found");

    const updated = {
      ...quotes[index],
      status: "quoted",
      quotedPrice: payload.quotedPrice,
      currency: payload.currency || quotes[index].currency || "USD",
      scope: payload.scope,
      proposedStartDate: payload.proposedStartDate,
      proposedEndDate: payload.proposedEndDate,
      validUntil: payload.validUntil,
      notes: payload.notes || "",
      quotedAt: new Date().toISOString()
    };

    quotes[index] = updated;
    saveLocalQuotes(quotes);
    return normalizeQuoteRequest(updated);
  }
};

// 5. Customer Accepts Quote (triggers booking creation)
export const acceptQuote = async (id) => {
  try {
    const response = await authApi.patch(`/quote-requests/${id}/accept`);
    const data = response.data.data || response.data;
    return data;
  } catch (error) {
    console.warn(`Backend API PATCH /quote-requests/${id}/accept not reachable. Updating local storage.`, error);

    const quotes = getLocalQuotes();
    const index = quotes.findIndex(q => q.id === id || q.quoteRequestId === id);
    if (index === -1) throw new Error("Quote request not found");

    const quoteReq = quotes[index];

    // Create corresponding booking in local storage
    let bookingId = 'BK-Q-' + Math.floor(100000 + Math.random() * 900000);
    try {
      const createdBkg = await createBooking({
        offeringId: 'off_custom_quote',
        scheduledAt: quoteReq.proposedStartDate ? `${quoteReq.proposedStartDate}T10:00:00Z` : new Date().toISOString(),
        durationHours: 8,
        numberOfPeople: 1,
        customerNotes: `Accepted Quote Scope: ${quoteReq.scope}`,
        pricingSnapshot: {
          basePrice: quoteReq.quotedPrice || 0,
          calculatedTotal: quoteReq.quotedPrice || 0,
          currency: quoteReq.currency || "USD"
        }
      });
      if (createdBkg?.bookingId) bookingId = createdBkg.bookingId;
    } catch (e) {
      console.warn("Could not create linked booking automatically:", e);
    }

    const updated = {
      ...quoteReq,
      status: "accepted",
      bookingId,
      acceptedAt: new Date().toISOString()
    };

    quotes[index] = updated;
    saveLocalQuotes(quotes);
    return { quoteRequestId: id, status: "accepted", bookingId, paymentStatus: "unpaid" };
  }
};

// 6. Decline Quote (Customer or Provider)
export const declineQuote = async (id, reason, message) => {
  try {
    const response = await authApi.patch(`/quote-requests/${id}/decline`, { reason, message });
    return response.data.data || response.data;
  } catch (error) {
    console.warn(`Backend API PATCH /quote-requests/${id}/decline not reachable. Updating local storage.`, error);

    const quotes = getLocalQuotes();
    const index = quotes.findIndex(q => q.id === id || q.quoteRequestId === id);
    if (index === -1) throw new Error("Quote request not found");

    const updated = {
      ...quotes[index],
      status: "declined",
      declineReason: reason,
      declineMessage: message || "",
      declinedAt: new Date().toISOString()
    };

    quotes[index] = updated;
    saveLocalQuotes(quotes);
    return { id, status: "declined", declinedBy: "user" };
  }
};

// 7. Get Messages in Quote Thread
export const getQuoteMessages = async (id) => {
  try {
    const response = await authApi.get(`/quote-requests/${id}/messages`);
    return response.data.data || response.data;
  } catch (error) {
    console.warn(`Backend API GET /quote-requests/${id}/messages not reachable. Using local storage.`, error);
    const key = `quote_messages_${id}`;
    const local = localStorage.getItem(key);
    return local ? JSON.parse(local) : [
      {
        id: "msg-init",
        quoteRequestId: id,
        senderId: "system",
        senderRole: "system",
        type: "system",
        body: "Negotiation thread initialized. Send a message to start.",
        attachmentUrls: [],
        sentAt: new Date().toISOString()
      }
    ];
  }
};

// 8. Send Chat Message to Thread
export const sendQuoteMessage = async (id, payload) => {
  try {
    const response = await authApi.post(`/quote-requests/${id}/messages`, payload);
    return response.data.data || response.data;
  } catch (error) {
    console.warn(`Backend API POST /quote-requests/${id}/messages not reachable. Using local storage.`, error);
    const key = `quote_messages_${id}`;
    const local = localStorage.getItem(key);
    const messages = local ? JSON.parse(local) : [];
    
    const newMsg = {
      id: 'msg-' + Math.floor(100000 + Math.random() * 900000),
      quoteRequestId: id,
      senderId: localStorage.getItem("userId") || "current-user",
      senderRole: localStorage.getItem("role") === "provider" ? "provider" : "customer",
      type: "message",
      body: payload.body,
      attachmentUrls: payload.attachmentUrls || [],
      sentAt: new Date().toISOString()
    };
    
    messages.push(newMsg);
    localStorage.setItem(key, JSON.stringify(messages));
    return newMsg;
  }
};

// 9. Send Revised Quote (Provider Only)
export const sendRevisedQuote = async (id, payload) => {
  try {
    const response = await authApi.post(`/quote-requests/${id}/revised-quote`, payload);
    return response.data.data || response.data;
  } catch (error) {
    console.warn(`Backend API POST /quote-requests/${id}/revised-quote not reachable. Using local storage.`, error);
    
    const quotes = getLocalQuotes();
    const idx = quotes.findIndex(q => q.id === id || q.quoteRequestId === id);
    if (idx !== -1) {
      quotes[idx] = {
        ...quotes[idx],
        quotedPrice: payload.quotedPrice,
        currency: payload.currency,
        scope: payload.scope,
        proposedStartDate: payload.proposedStartDate,
        proposedEndDate: payload.proposedEndDate,
        validUntil: payload.validUntil,
        notes: payload.notes,
        status: "quoted",
        updatedAt: new Date().toISOString()
      };
      saveLocalQuotes(quotes);
    }

    const verKey = `quote_versions_${id}`;
    const localVers = localStorage.getItem(verKey);
    const versions = localVers ? JSON.parse(localVers) : [];
    const newVerNum = versions.length + 1;
    const newVer = {
      id: 'ver-' + Math.floor(100000 + Math.random() * 900000),
      versionNumber: newVerNum,
      quotedPrice: payload.quotedPrice,
      currency: payload.currency,
      scope: payload.scope,
      proposedStartDate: payload.proposedStartDate,
      proposedEndDate: payload.proposedEndDate,
      validUntil: payload.validUntil,
      notes: payload.notes,
      status: "pending",
      createdAt: new Date().toISOString()
    };
    
    versions.forEach(v => { if (v.status === 'pending') v.status = 'superseded'; });
    versions.push(newVer);
    localStorage.setItem(verKey, JSON.stringify(versions));

    const msgKey = `quote_messages_${id}`;
    const localMsgs = localStorage.getItem(msgKey);
    const messages = localMsgs ? JSON.parse(localMsgs) : [];
    messages.push({
      id: 'msg-' + Math.floor(100000 + Math.random() * 900000),
      quoteRequestId: id,
      senderId: localStorage.getItem("userId") || "prov-1",
      senderRole: "provider",
      type: "quote",
      body: `Provider sent revised quote version ${newVerNum} for ${payload.currency} ${payload.quotedPrice}`,
      quoteVersionId: newVer.id,
      quoteVersion: newVer,
      sentAt: new Date().toISOString()
    });
    localStorage.setItem(msgKey, JSON.stringify(messages));

    return normalizeQuoteRequest(quotes[idx]);
  }
};

// 10. Get Quote Version History
export const getQuoteVersions = async (id) => {
  try {
    const response = await authApi.get(`/quote-requests/${id}/quotes`);
    return response.data.data || response.data;
  } catch (error) {
    console.warn(`Backend API GET /quote-requests/${id}/quotes not reachable. Using local storage.`, error);
    const key = `quote_versions_${id}`;
    const local = localStorage.getItem(key);
    if (local) return JSON.parse(local);

    const quotes = getLocalQuotes();
    const qr = quotes.find(q => q.id === id || q.quoteRequestId === id);
    if (qr && qr.quotedPrice) {
      const v1 = {
        id: "ver-1-local",
        versionNumber: 1,
        quotedPrice: qr.quotedPrice,
        currency: qr.currency,
        scope: qr.scope,
        proposedStartDate: qr.proposedStartDate,
        proposedEndDate: qr.proposedEndDate,
        validUntil: qr.validUntil,
        notes: qr.notes,
        status: qr.status === 'accepted' ? 'accepted' : (qr.status === 'declined' ? 'declined' : 'pending'),
        createdAt: qr.createdAt
      };
      return [v1];
    }
    return [];
  }
};

// 11. Close Negotiation (Provider Only)
export const closeNegotiation = async (id, payload) => {
  try {
    const response = await authApi.patch(`/quote-requests/${id}/close`, payload);
    return response.data.data || response.data;
  } catch (error) {
    console.warn(`Backend API PATCH /quote-requests/${id}/close not reachable. Using local storage.`, error);
    const quotes = getLocalQuotes();
    const index = quotes.findIndex(q => q.id === id || q.quoteRequestId === id);
    if (index === -1) throw new Error("Quote request not found");

    const updated = {
      ...quotes[index],
      status: "closed",
      declineReason: "Negotiation closed by provider",
      declineMessage: payload.reason || "",
      updatedAt: new Date().toISOString()
    };
    quotes[index] = updated;
    saveLocalQuotes(quotes);

    const verKey = `quote_versions_${id}`;
    const localVers = localStorage.getItem(verKey);
    if (localVers) {
      const versions = JSON.parse(localVers);
      versions.forEach(v => { if (v.status === 'pending') v.status = 'declined'; });
      localStorage.setItem(verKey, JSON.stringify(versions));
    }

    const msgKey = `quote_messages_${id}`;
    const localMsgs = localStorage.getItem(msgKey);
    const messages = localMsgs ? JSON.parse(localMsgs) : [];
    messages.push({
      id: 'msg-' + Math.floor(100000 + Math.random() * 900000),
      quoteRequestId: id,
      senderId: localStorage.getItem("userId") || "prov-1",
      senderRole: "provider",
      type: "system",
      body: `Provider closed the negotiation. Reason: ${payload.reason || "No reason specified."}`,
      sentAt: new Date().toISOString()
    });
    localStorage.setItem(msgKey, JSON.stringify(messages));

    return normalizeQuoteRequest(updated);
  }
};
